import React, { useState } from 'react';
import { 
  Search, 
  Bot, 
  Send, 
  ArrowLeft, 
  CheckCircle2, 
  Sparkles, 
  ExternalLink, 
  ShieldCheck, 
  HelpCircle,
  FileText,
  Calculator,
  ArrowRight
} from 'lucide-react';
import { AppView } from '../../types';
import { parseIntentDeterministic } from '../../services/ai/intentEngine';
import { executeToolCall } from '../../services/ai/toolRegistry';
import { retrieveTrustedChunks } from '../../services/ai/ragEngine';

interface AiSearchAssistantModalProps {
  onNavigateToView: (view: AppView) => void;
}

interface Message {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  toolResult?: any;
  citations?: any[];
  timestamp: string;
}

export const AiSearchAssistantModal: React.FC<AiSearchAssistantModalProps> = ({ onNavigateToView }) => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      sender: 'assistant',
      text: 'Bonjour ! Je suis l\'assistant d\'intelligence financière YamooPay. Posez votre question ou indiquez un calcul (ex: "Salaire net 120000$ en Californie", "USD to EUR", "Prêt immobilier 400000$").',
      timestamp: new Date().toLocaleTimeString(),
    }
  ]);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!query.trim() || isProcessing) return;

    const userText = query.trim();
    const userMsg: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text: userText,
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setQuery('');
    setIsProcessing(true);

    try {
      // 1. Intent Detection
      const intentPlan = parseIntentDeterministic(userText);
      
      // 2. Execute deterministic tool if matched
      let toolData = null;
      if (intentPlan.requiredTools.length > 0) {
        const toolId = intentPlan.requiredTools[0];
        // Extract mock amount or parameters if present
        const amountMatch = userText.match(/\d+/);
        const amount = amountMatch ? parseInt(amountMatch[0], 10) : undefined;
        toolData = await executeToolCall(toolId, { amount, salary: amount, principal: amount });
      }

      // 3. Retrieve Trusted RAG Chunks
      const chunks = retrieveTrustedChunks(userText);

      // 4. Compose Assistant response
      let responseText = `J'ai analysé votre requête via le moteur d'intention **${intentPlan.primaryIntent}** (Confiance: ${intentPlan.confidence}).`;
      if (toolData && toolData.success) {
        responseText += `\n\n**Résultat du moteur déterministe YamooPay :**\n\`\`\`json\n${JSON.stringify(toolData.result, null, 2)}\n\`\`\``;
      } else {
        responseText += `\n\nVoici les informations vérifiées issues de notre base de connaissances.`;
      }

      const assistantMsg: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'assistant',
        text: responseText,
        toolResult: toolData,
        citations: chunks.map(c => ({ title: c.sourceTitle, tier: c.trustTier })),
        timestamp: new Date().toLocaleTimeString(),
      };

      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        sender: 'assistant',
        text: 'Une erreur est survenue lors de l\'orchestration de la requête.',
        timestamp: new Date().toLocaleTimeString(),
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-[#1A1A1A] pb-16 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('homepage')}
              className="p-2 bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-xl text-[#1A1A1A] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="text-[11px] font-extrabold uppercase tracking-widest text-[#0E7044]">
                Ask YamooPay AI Financial Assistant (Prompt 15)
              </div>
              <h1 className="text-xl font-black tracking-tight text-[#1A1A1A]">
                Recherche Globale & Orchestration Intelligente
              </h1>
            </div>
          </div>
          <button
            onClick={() => onNavigateToView('ai-command-center')}
            className="px-3 py-1.5 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] rounded-xl text-xs font-bold transition-colors cursor-pointer"
          >
            AI Command Center
          </button>
        </div>
      </div>

      {/* Chat / Assistant Feed */}
      <div className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-6 space-y-6 overflow-y-auto">
        {/* Quick Suggestion Pills */}
        <div className="flex flex-wrap gap-2 pb-2">
          {[
            'Salaire net 120000$ en Californie',
            'Convertir 500 USD en EUR',
            'Prêt immobilier 400000$ à 6%',
            'Qu\'est-ce que le taux APR ?',
          ].map((suggestion, idx) => (
            <button
              key={idx}
              onClick={() => {
                setQuery(suggestion);
              }}
              className="px-3 py-1.5 bg-white border border-[#E5E7EB] hover:border-[#0E7044] rounded-full text-xs font-medium text-[#4B5563] hover:text-[#1A1A1A] transition-colors shadow-2xs cursor-pointer flex items-center gap-1.5"
            >
              <Sparkles className="w-3 h-3 text-[#0E7044]" />
              <span>{suggestion}</span>
            </button>
          ))}
        </div>

        {messages.map((m) => (
          <div key={m.id} className={`flex gap-4 ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.sender === 'assistant' && (
              <div className="w-8 h-8 rounded-xl bg-[#1A1A1A] text-white flex items-center justify-center shrink-0 font-bold text-xs shadow-xs">
                YP
              </div>
            )}
            <div className={`max-w-2xl rounded-2xl p-4 text-sm leading-relaxed shadow-2xs ${
              m.sender === 'user' 
                ? 'bg-[#1A1A1A] text-white rounded-br-xs' 
                : 'bg-white border border-[#E5E7EB] text-[#1A1A1A] rounded-bl-xs'
            }`}>
              <div className="whitespace-pre-wrap font-sans">{m.text}</div>

              {m.citations && m.citations.length > 0 && (
                <div className="mt-4 pt-3 border-t border-[#E5E7EB] text-xs">
                  <span className="font-bold text-[#6B7280] block mb-1">Sources & Citations Vérifiées :</span>
                  <div className="space-y-1">
                    {m.citations.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-[#0E7044] font-medium">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>{c.title}</span>
                        <span className="px-1.5 py-0.5 bg-emerald-50 rounded text-[10px] font-mono">{c.tier}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {m.sender === 'user' && (
              <div className="w-8 h-8 rounded-xl bg-emerald-700 text-white flex items-center justify-center shrink-0 font-bold text-xs shadow-xs">
                U
              </div>
            )}
          </div>
        ))}

        {isProcessing && (
          <div className="flex gap-4 items-center">
            <div className="w-8 h-8 rounded-xl bg-[#1A1A1A] text-white flex items-center justify-center shrink-0 font-bold text-xs shadow-xs animate-pulse">
              YP
            </div>
            <div className="bg-white border border-[#E5E7EB] px-4 py-3 rounded-2xl text-xs text-[#6B7280] flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#0E7044] animate-spin" />
              <span>YamooPay orchestre les moteurs déterministes et vérifie les sources...</span>
            </div>
          </div>
        )}
      </div>

      {/* Input Bar */}
      <div className="bg-white border-t border-[#E5E7EB] p-4">
        <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Posez une question financière ou calculez (ex: 80k salary California)..."
              className="w-full bg-[#F9FAFB] border border-[#E5E7EB] rounded-2xl pl-11 pr-4 py-3 text-sm text-[#1A1A1A] focus:outline-hidden focus:border-[#0E7044] transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={!query.trim() || isProcessing}
            className="px-6 py-3 bg-[#1A1A1A] hover:bg-black disabled:opacity-50 text-white rounded-2xl text-xs font-bold transition-all shadow-xs flex items-center gap-2 cursor-pointer"
          >
            <span>Envoyer</span>
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>
      </div>
    </div>
  );
};
